import { useGame } from '../state/GameContext';
import { useGameActions } from '../hooks/useGameActions';
import { API_BASE } from '../api/config';

export default function GameScreen() {
  const { state } = useGame();
  const { submitGuess } = useGameActions();

  if (!state.currentPhoto) {
    return (
      <div className="text-center p-5">
        <div className="text-2xl mb-2">Get Ready!</div>
        <p className="text-sm opacity-90">Loading next photo...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Photo counter + Timer */}
      <div className="flex justify-between items-center mb-3 px-3 py-2 bg-white/8 rounded-xl text-center">
        <span className="font-semibold">
          Photo {state.currentPhoto.photoIndex}/{state.currentPhoto.totalPhotos}
        </span>
        <span
          className="text-xl font-bold"
          style={{ color: state.timeRemaining <= 5 ? '#ff4444' : '#fff' }}
        >
          {state.timeRemaining}s
        </span>
      </div>

      <img
        src={state.currentPhoto.photoUrl.startsWith('http') ? state.currentPhoto.photoUrl : `${API_BASE}${state.currentPhoto.photoUrl}`}
        alt="Player's submission to identify"
        className="game-photo"
      />

      {!state.photoResults && (
        <>
          <h3 className="text-center mb-3 text-lg font-semibold">
            Whose photo is this?
          </h3>

          {!state.hasSubmittedGuess && (
            <div className="players-grid">
              {state.currentPhoto.players.map((player) => (
                <button
                  key={player.id}
                  className="player-button"
                  onClick={() => submitGuess(player.id)}
                >
                  {player.name}
                </button>
              ))}
            </div>
          )}

          {state.hasSubmittedGuess && (
            <div className="text-center p-5 bg-green-500/15 rounded-2xl border-2 border-green-500/40 mt-3">
              <div className="text-4xl mb-2">✓</div>
              <div className="text-lg font-semibold text-green-200">
                Guess Submitted!
              </div>
              <div className="text-sm mt-2 opacity-90">
                Waiting for other players...
              </div>
            </div>
          )}
        </>
      )}

      {state.photoResults && (
        <div className="combined-results-card">
          {/* Confetti Effect */}
          {state.showConfetti && (
            <div className="confetti-container">
              {Array.from({ length: 50 }).map((_, i) => (
                <div
                  key={i}
                  className="confetti"
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    backgroundColor: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f7b731', '#5f27cd', '#00d2d3'][Math.floor(Math.random() * 6)]
                  }}
                />
              ))}
            </div>
          )}

          {/* Correct Answer Header */}
          <div className="results-header">
            <h3 className="text-xl mb-0">
              Correct: {state.photoResults.correctPlayer}
            </h3>
          </div>

          {/* Round Results */}
          <div className="round-results-section">
            <h4 className="mb-3 text-base opacity-90">
              Round Results
            </h4>
            {state.photoResults.guesses.map((guess, index) => (
              <div
                key={index}
                className={`flex justify-between items-center px-2.5 py-2 mb-1.5 rounded-xl border-2 text-sm ${
                  guess.correct
                    ? 'bg-green-500/15 border-green-500/40'
                    : 'bg-red-500/15 border-red-500/30'
                }`}
              >
                <span className="font-semibold">
                  {guess.correct ? '✓' : '✗'} {guess.guesser}
                </span>
                <span className="opacity-90 text-xs">
                  → {guess.guessed}
                </span>
                <span className="text-xs opacity-80 bg-white/10 px-1.5 py-0.5 rounded-lg">
                  {guess.timeToAnswer?.toFixed(1)}s
                </span>
              </div>
            ))}
          </div>

          {/* Current Leaderboard */}
          {state.photoResults.leaderboard && state.photoResults.leaderboard.length > 0 && (
            <div className="leaderboard mt-4">
              <h4 className="mb-3 text-base opacity-90 text-center">
                Current Standings
              </h4>
              {state.photoResults.leaderboard.map((player, index) => {
                const isCurrentPlayer = player.id === state.playerId;
                const previousScore = state.previousScores.get(player.id) || 0;
                const scoreChange = player.score - previousScore;
                const rankClass = index < 3 ? `rank-${index + 1}` : '';

                return (
                  <div
                    key={index}
                    className={`leaderboard-item ${rankClass}`}
                    style={isCurrentPlayer ? {
                      background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.25) 0%, rgba(126, 34, 206, 0.2) 100%)',
                      border: '3px solid rgba(147, 51, 234, 0.6)',
                      boxShadow: '0 0 20px rgba(147, 51, 234, 0.4)',
                    } : {}}
                  >
                    <span className="rank-badge">
                      {index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `#${index + 1}`}
                    </span>
                    <span className="player-name">
                      {player.name}
                      {isCurrentPlayer && (
                        <span className="ml-2 text-xs bg-purple-500/40 px-1.5 py-0.5 rounded-full font-bold">YOU</span>
                      )}
                      {player.streak && player.streak >= 3 && (
                        <span className="ml-2 text-xs bg-orange-500/30 px-1.5 py-0.5 rounded-full">
                          {player.streak} streak
                        </span>
                      )}
                    </span>
                    <span className="player-score">
                      {player.score}
                      {scoreChange > 0 && (
                        <span className="text-sm text-green-400 ml-1">+{scoreChange}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Countdown to next photo */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-semibold">
              <span>Next photo in</span>
              <span className="text-lg font-bold" style={{ color: state.resultsCountdown <= 2 ? '#ff4444' : 'var(--color-electric-blue)' }}>
                {state.resultsCountdown}s
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
